import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { uploadFile } from '@nx-frontend/shared/utils';
import { isFileAllowed } from './agent-mode/is-file-allowed';
import { ChatBotAttachedFile, ChatBotAttachedFileUploadingState } from './types';

export const useChatbotFileUpload = () => {
  const [files, setFiles] = useState<ChatBotAttachedFile[]>([]);
  const { toast } = useToast();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const notAllowedFile = acceptedFiles.find((file) => !isFileAllowed(file));
      if (notAllowedFile) {
        toast({
          title: 'File is not allowed',
          description: `${notAllowedFile.name} is not allowed`,
        });
        return;
      }

      const attachedFiles = acceptedFiles.map((file) => {
        uploadFile(file, {
          folder: 'ai_agent',
          onProgress: (progress) => {
            setFiles((prevFiles) =>
              prevFiles.map((prevFile) =>
                prevFile.file === file ? { ...prevFile, uploaded: progress.loaded } : prevFile
              )
            );
          },
          onCancelFn: (cancelFn) => {
            setFiles((prevFiles) =>
              prevFiles.map((prevFile) => (prevFile.file === file ? { ...prevFile, cancelUpload: cancelFn } : prevFile))
            );
          },
        })
          .then((tempStoredFile) => {
            if (tempStoredFile) {
              setFiles((prevFiles) =>
                prevFiles.map((prevFile) =>
                  prevFile.file === file
                    ? {
                        ...prevFile,
                        uploadState: 'uploaded' as ChatBotAttachedFileUploadingState,
                        tempStoredFileName: tempStoredFile.name,
                      }
                    : prevFile
                )
              );
            } else {
              // to temp stored file? consider upload failed
              setFiles((prevFiles) =>
                prevFiles.map((prevFile) =>
                  prevFile.file === file
                    ? { ...prevFile, uploadState: 'error' as ChatBotAttachedFileUploadingState }
                    : prevFile
                )
              );
            }
          })
          .catch((err) => {
            console.error('Chatbot file upload failed', err);
            setFiles((prevFiles) =>
              prevFiles.map((prevFile) =>
                prevFile.file === file
                  ? { ...prevFile, uploadState: 'error' as ChatBotAttachedFileUploadingState }
                  : prevFile
              )
            );
          });

        return {
          file,
          uploaded: 0,
          uploadState: 'uploading' as ChatBotAttachedFileUploadingState,
        };
      });

      setFiles(files.concat(attachedFiles));
    },
    [files, toast]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true,
    preventDropOnDocument: false,
  });

  const removeFile = useCallback((file: ChatBotAttachedFile) => {
    setFiles((prevFiles) => prevFiles.filter((prevFile) => prevFile.file !== file.file));
    if (file.cancelUpload) {
      file.cancelUpload();
    }
  }, []);

  const resetFiles = useCallback(() => {
    setFiles([]);
  }, []);

  const getRootPropsWrapped = useCallback(() => {
    const rootProps = getRootProps();

    // deleting focus handler as it prevents clicking on links, NIM-37421
    delete rootProps.onFocus;
    delete rootProps.onClick;
    delete rootProps.onBlur;
    return rootProps;
  }, [getRootProps]);

  const addFiles = useCallback(
    (files: File[]) => {
      onDrop(files);
    },
    [onDrop]
  );

  return {
    getRootProps: getRootPropsWrapped,
    getInputProps,
    isDragActive,
    open,
    files,
    removeFile,
    resetFiles,
    addFiles,
  };
};
